import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Building2, Shield, Sparkles, Clock } from "lucide-react";
import InstitutionBillingCard from "@/components/InstitutionBillingCard";
import AdvancedUpgradeCard from "@/components/subscription/AdvancedUpgradeCard";
import StripeCheckoutCard from "@/components/subscription/StripeCheckoutCard";
import { isTrueInstitutional } from "@/lib/tier";

/**
 * Subscription page — 2026-09-01 universal-trial model.
 *
 * Four user states:
 *   • Trial (tier='advanced', trial_status ACTIVE/GRACE) → everyone who
 *     just signed up. Full access, countdown + subscribe CTA, no card on
 *     file yet.
 *   • Individual (tier='individual', trial finished, didn't subscribe)
 *     → FREE FOREVER, reduced feature set, can still subscribe any time.
 *   • Institution Admin  → role='broker'. Bank transfer + admin verify.
 *                          Renders the InstitutionBillingCard.
 *   • Bundled seat       → tier='moe_institution'/'cma_institution' but
 *                          role='user'. Their broker handles billing.
 */
export default function Subscription() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [trial, setTrial] = useState(null);

    useEffect(() => {
        let cancel = false;
        api.get("/subscription/my-status")
            .then((r) => { if (!cancel) setStatus(r.data); })
            .catch(() => { if (!cancel) setStatus(null); });
        api.get("/trial/status")
            .then((r) => { if (!cancel) setTrial(r.data); })
            .catch(() => { if (!cancel) setTrial(null); });
        return () => { cancel = true; };
    }, []);

    const isInstitutionAdmin = user?.role === "broker";
    const isInstitutional    = isTrueInstitutional(user);
    const isTrialActive      = Boolean(trial?.active) && user?.tier === "advanced";
    const inst_label = user?.tier === "cma_institution" ? "CMA-regulated brokerage seat" :
                       user?.tier === "moe_institution" ? "MoE-partner education seat" : null;

    // Institution admins get a thin, bank-transfer-first billing surface.
    if (isInstitutionAdmin) {
        return (
            <div className="max-w-4xl">
                <div className="mb-6">
                    <div className="overline text-primary">Billing</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                        Institution billing
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Pay via direct bank transfer. Our super-admin verifies within one business day.
                    </p>
                </div>
                <InstitutionBillingCard />
            </div>
        );
    }

    return (
        <>
            <div className="mb-6">
                <div className="overline text-primary">Subscription &amp; billing</div>
                {isTrialActive ? (
                    <>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="subscription-trial-headline">
                            You&apos;re on your <span className="text-primary">7-day Obondo Advanced free trial</span>
                            {trial.status !== "GRACE" && <> &middot; {trial.days_remaining} day{trial.days_remaining === 1 ? "" : "s"} left</>}.
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2 max-w-3xl flex items-start gap-1.5">
                            <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                            No card is on file and nothing will be auto-charged. Full Obondo access — leaderboards,
                            AI copilot, behavioural coaching — is unlocked right now. Subscribe any time to keep it
                            after your trial ends; if you don&apos;t, you&apos;ll simply move to the free Individual plan.
                        </p>
                    </>
                ) : isInstitutional ? (
                    <>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="subscription-institutional-headline">
                            Your seat is covered by <span className="text-accent">your broker&apos;s Institutional plan</span>.
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                            Nothing to pay directly — billing is handled at the institution level.
                        </p>
                    </>
                ) : (
                    <>
                        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                            You&apos;re on the Individual plan &middot; <span className="text-primary">free forever</span>.
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                            Melian Dialogue is free forever for individual traders. No card on file — you have full
                            access to the terminal, NSE + FX + Commodities + Options, Kimani (our rule-based FAQ
                            assistant), community, and copy-trading.
                        </p>
                    </>
                )}
            </div>

            {/* Individual free-forever card */}
            {!isInstitutional && !isTrialActive && (
                <>
                <div className="border-2 border-primary rounded-xl bg-card p-6 md:p-8 mb-6" data-testid="individual-free-card">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs uppercase font-bold tracking-wider text-primary mb-1">Individual · Free forever</div>
                            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">KES 0<span className="text-base font-normal text-muted-foreground">/month</span></h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                Full-featured. No billing anniversary. No hidden gate.
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {[
                            "Full NSE + FX + Commodities + Options terminal",
                            "Live simulated market feed (WebSocket)",
                            "5 concurrent open positions",
                            "90 days of OHLC history",
                            "Community: post, comment, copy",
                            "Kimani — rule-based FAQ assistant",
                            "Public leaderboard & Trader-of-the-Week",
                            "PWA install · mobile + desktop",
                        ].map((f) => (
                            <div key={f} className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                                <span>{f}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Global card-payment rail (Stripe) — alongside M-Pesa Paybill */}
                <div className="mb-6"><StripeCheckoutCard /></div>
                </>
            )}

            {/* Institutional-seat card (shown only if the user IS on a true institutional plan) */}
            {isInstitutional && (
                <div className="border-2 border-accent rounded-xl bg-card p-6 md:p-8 mb-6" data-testid="institutional-seat-card">
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-12 h-12 rounded-lg bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <div className="text-xs uppercase font-bold tracking-wider text-accent mb-1">
                                Institutional · {inst_label}
                            </div>
                            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                                Provided by your broker
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {status?.agency_name
                                    ? <>Your seat is covered by <span className="font-semibold text-foreground">{status.agency_name}</span>. There&apos;s nothing to pay directly — your broker handles the plan.</>
                                    : <>Your seat is bundled with your broker&apos;s Institutional subscription. There&apos;s nothing to pay directly.</>}
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {[
                            "Everything on the Individual plan, plus:",
                            "Obondo — English · Kiswahili · Sheng AI copilot",
                            "20 concurrent open positions",
                            "365 days of OHLC history",
                            "Autopilot: advisory · semi-auto · fully autonomous",
                            "MVO portfolio rebalance + backtester",
                            "Broker dashboard visibility (if applicable)",
                            "Priority in-portal support routing",
                        ].map((f) => (
                            <div key={f} className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-accent flex-shrink-0" />
                                <span>{f}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Advanced Trader upgrade / subscribe card — retail-only. Also
                shown DURING the trial so there's always a way to convert
                before day 7. Handles inactive/grace/active/cancelled
                states internally. */}
            {!isInstitutional && <AdvancedUpgradeCard />}

            {/* Info about the Institutional tier — visible to individuals so they know it exists */}
            {!isInstitutional && (
                <div className="border border-border rounded-lg bg-card/50 p-5 mb-6" data-testid="institutional-info-card">
                    <div className="flex items-start gap-3 mb-3">
                        <Building2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-bold text-base">Are you a broker, university, or MoE-partner institution?</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                Institutional seats include Obondo (our full AI copilot), unlimited history,
                                20-position limit, and Autopilot. Two variants:
                            </p>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-4">
                        <div className="border border-border rounded p-3 bg-background">
                            <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">MoE Institution</div>
                            <div className="font-mono text-lg font-bold mt-1">KES 30,000/mo · KES 350,000/yr</div>
                            <div className="text-xs text-muted-foreground mt-1">Universities, colleges, MoE-partner schools</div>
                        </div>
                        <div className="border border-border rounded p-3 bg-background">
                            <div className="text-xs uppercase font-bold tracking-wider text-muted-foreground">CMA Institution</div>
                            <div className="font-mono text-lg font-bold mt-1">KES 50,000/mo · KES 500,000/yr</div>
                            <div className="text-xs text-muted-foreground mt-1">CMA-regulated brokerages &amp; investment firms</div>
                        </div>
                    </div>
                    <div className="mt-4 text-xs text-muted-foreground">
                        Both plans are paid via <span className="font-semibold">bank transfer + admin verification</span>.
                        Sign up your institution at <a href="/for-brokers" className="text-primary underline">/for-brokers</a>.
                    </div>
                </div>
            )}

            {/* Closed-loop guarantee footer */}
            <div className="text-xs text-muted-foreground flex items-center justify-center gap-2 mt-8">
                <Shield className="w-3 h-3" />
                Closed-loop platform · No card storage · No third-party payment redirect · All comms in-portal
            </div>
        </>
    );
}
